import { ALLOWED_CANCEL_METHODS, ALLOWED_CANCEL_STATES } from '../../constants'
import {
  AccountDetailResponsePayload,
  AchRelationshipResponsePayload,
  CashDirection,
  PermissionRequestResult,
  SearchCashTransfersResponsePayload,
  TerminalCashTransferStates,
  TransferMechanism
} from '@Types'
import { Box, IconButton, Link, useDisclosure } from '@chakra-ui/react'
import React, { useCallback, useMemo, useState } from 'react'
import { Table, TableColumn, TableRecord } from '@apex/component-library'
import { capitalizeWord, removeUnderscores, useFormatNumber } from '../../utils'
import { isEmpty, isNil, startCase } from 'lodash'

import { CancelTransferModal } from '../../components/CancelTransferModal'
import { DateTime } from 'luxon'
import { IoClose } from 'react-icons/io5'
import { hasRtpDepositCancelPermissions, hasRtpWithdrawalCancelPermissions } from '../../utils/permissionsHelper'

const isAuthorizedToCancel = (
  method: TransferMechanism,
  direction: CashDirection | undefined,
  userPermissions: PermissionRequestResult[] | null | undefined
) => {
  switch (method) {
    case TransferMechanism.Rtp:
      if (direction === CashDirection.Deposit) {
        return hasRtpDepositCancelPermissions(userPermissions)
      }
      if (direction === CashDirection.Withdrawal) {
        return hasRtpWithdrawalCancelPermissions(userPermissions)
      }
      return false

    default:
      return true //TODO: Once all transfer types are accounted for, this should return false for principal of least privilege
  }
}

const isCancelableTransfer = (method: TransferMechanism, state: TerminalCashTransferStates) => {
  return ALLOWED_CANCEL_METHODS.has(method) && ALLOWED_CANCEL_STATES.has(state)
}

const buildTransferType = (transfer: SearchCashTransfersResponsePayload, accountNumber: string) => {
  const method = removeUnderscores(transfer?.method) || ''
  let direction: string = capitalizeWord(transfer?.direction) || ''

  if (!isEmpty(transfer?.iraContributionDetails)) {
    direction = 'IRA Contribution'
  }

  if (!isEmpty(transfer?.iraDistributionDetails)) {
    direction = 'IRA Distribution'
  }

  if (
    transfer?.method === TransferMechanism.Journal &&
    transfer?.direction !== CashDirection.Withdrawal &&
    transfer?.direction !== CashDirection.Deposit
  ) {
    if (accountNumber === transfer?.cashJournalDetails?.account) {
      direction = 'Debit'
    } else {
      direction = 'Credit'
    }
  }

  return method === TransferMechanism.Fee || method === TransferMechanism.Credit ? method : `${method} ${direction}`
}

const columns: TableColumn[] = [
  {
    key: 'date',
    label: 'Date',
    recordField: 'date',
    isSortable: false
  },
  {
    key: 'type',
    label: 'Type',
    recordField: 'type',
    isSortable: false
  },
  {
    key: 'amount',
    label: 'Amount',
    recordField: 'amount',
    isSortable: false,
    alignment: 'right'
  },
  {
    key: 'status',
    label: 'Status',
    recordField: 'status',
    isSortable: false
  },
  {
    key: 'cancel',
    label: 'Cancel',
    recordField: 'cancel',
    isSortable: false,
    alignment: 'right',
    cellRenderer: ({ value }: { value: JSX.Element }) => value
  }
]

type SelectedTransfer = { transfer: SearchCashTransfersResponsePayload; type: string }

interface Props {
  searchTransfers?: SearchCashTransfersResponsePayload[]
  accountNumber: string
  achRelationships?: AchRelationshipResponsePayload[] | null
  accountDetails?: AccountDetailResponsePayload | null
  permissions?: PermissionRequestResult[] | null | undefined
}

const TransfersTable = ({ searchTransfers, accountNumber, achRelationships, accountDetails, permissions }: Props) => {
  const formatNumber = useCallback(useFormatNumber(), [])
  const [selectedTransfer, setSelectedTransfer] = useState<SelectedTransfer>()
  const { isOpen, onOpen, onClose } = useDisclosure()

  const searchResults = searchTransfers || []

  const tableData: TableRecord[] = useMemo(() => {
    const tableRowData: TableRecord[] = []
    for (let i = searchResults.length - 1; i >= 0; i--) {
      const transfer = searchResults[i]
      const type = buildTransferType(transfer, accountNumber)

      const date = transfer?.initiationTime
        ? DateTime.fromISO(transfer?.initiationTime).toLocaleString(DateTime.DATE_SHORT)
        : ''

      const amount = isNil(transfer?.amount)
        ? ''
        : formatNumber(transfer?.amount, {
            style: 'currency',
            currency: 'USD'
          })

      const openModal = (transferData: SelectedTransfer) => {
        setSelectedTransfer(transferData)
        onOpen()
      }

      const canCancel =
        !isNil(transfer?.method) &&
        !isNil(transfer?.status) &&
        !isNil(accountNumber) &&
        isCancelableTransfer(transfer.method, transfer.status) &&
        isAuthorizedToCancel(transfer.method, transfer.direction, permissions)

      const cancelElement = canCancel ? (
        ((
          <IconButton
            variant='ghost'
            fontSize='20px'
            h='15px'
            minW='none'
            border='none'
            size='md'
            color='red'
            aria-label='Cancel Transfer'
            icon={<IoClose />}
            onClick={() => openModal({ transfer, type })}
          />
        ) as any)
      ) : (
        <Box as='span'>N/A</Box>
      )

      const statusLink = (
        <Link href={`/cashiering/details/${transfer.id}`} color='blue.400' target='_blank'>
          {startCase(transfer?.status)}
        </Link>
      ) as any

      tableRowData.push({
        date,
        type,
        amount,
        key: i,
        status: statusLink,
        cancel: cancelElement
      })
    }
    return tableRowData
  }, [accountNumber, formatNumber, searchResults, onOpen, permissions])

  return (
    <>
      <Table columns={columns} data={tableData} size='md'></Table>
      <CancelTransferModal
        achRelationships={achRelationships}
        accountDetails={accountDetails}
        accountNumber={accountNumber}
        transferData={selectedTransfer?.transfer}
        type={selectedTransfer?.type!}
        isOpen={isOpen}
        onClose={onClose}
      />
    </>
  )
}

export default TransfersTable
